import * as React from 'react';
import { useDispatch, useSelector, RootStateOrAny } from 'react-redux';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { useTranslation } from 'hooks';
import {
  ListCountriesActions,
  ListCountriesState,
} from 'store/ducks/country';
import {
  useZipcodeInternacionalSearchParams,
  type ZipcodeInternacionalFilters as Filters,
} from 'hooks/store/use-zipcode-internacional-search-params';
import { Form, FormField } from 'components/ui/form';
import { ComboboxGs, InputGs } from 'components/ui/Forms';
import { Button } from 'components/ui/ButtonGs';

import { translations } from '../translations';

const formSchema = z.object({
  general_country_id: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zipcode: z.string().optional(),
});

type FormSchema = z.infer<typeof formSchema>;

const EMPTY_FILTERS: FormSchema = {
  general_country_id: '',
  city: '',
  state: '',
  zipcode: '',
};

type Props = {
  onSubmitFilters: (filters: Filters) => void;
  onResetFilters: () => void;
};

export const ZipcodeInternacionalFilters = ({
  onSubmitFilters,
  onResetFilters,
}: Props) => {
  const dispatch = useDispatch();
  const { getTranslation: t } = useTranslation(translations);
  const { filters } = useZipcodeInternacionalSearchParams();

  const { data: countries, loading: loadingCountries } = useSelector<
    RootStateOrAny,
    ListCountriesState
  >((state) => state.listCountries);

  const countryOptions = React.useMemo(
    () =>
      countries.map((country) => ({
        name: country.label,
        value: String(country.value),
      })),
    [countries],
  );

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      ...EMPTY_FILTERS,
      ...filters,
    },
  });

  const resetForm = React.useCallback(() => {
    form.reset(EMPTY_FILTERS);
    onResetFilters();
  }, [form, onResetFilters]);

  React.useEffect(() => {
    dispatch(ListCountriesActions.request({ all: true, permission: true }));
  }, [dispatch]);

  React.useEffect(() => {
    form.reset({
      ...EMPTY_FILTERS,
      ...filters,
    });
  }, [filters, form]);

  return (
    <Form {...form}>
      <form
        className="w-full"
        onSubmit={form.handleSubmit(onSubmitFilters)}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <FormField
            control={form.control}
            name="general_country_id"
            render={({ field }) => (
              <ComboboxGs
                label={t('country')}
                form={form}
                fieldName={field.name}
                fieldValue={field.value}
                content={countryOptions}
                loading={loadingCountries}
                clearable
              />
            )}
          />
          <FormField
            control={form.control}
            name="city"
            render={({ field }) => (
              <InputGs {...field} value={field.value ?? ''} label={t('city')} />
            )}
          />
          <FormField
            control={form.control}
            name="state"
            render={({ field }) => (
              <InputGs {...field} value={field.value ?? ''} label={t('state')} />
            )}
          />
          <FormField
            control={form.control}
            name="zipcode"
            render={({ field }) => (
              <InputGs
                {...field}
                value={field.value ?? ''}
                label={t('zipcode')}
              />
            )}
          />
        </div>
        <div className="mt-6 flex gap-4">
          <Button.Root
            type="button"
            variant="secondary"
            size="sm"
            onClick={resetForm}
          >
            {t('resetButton')}
          </Button.Root>
          <Button.Root type="submit" size="sm">
            {t('submitButton')}
          </Button.Root>
        </div>
      </form>
    </Form>
  );
};
